import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { appendItalianDictation, normalizeItalianDictation } from "@/lib/italianDictation";

interface UseVoiceRecognitionResult {
  isSupported: boolean;
  isListening: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
  cancel: () => void;
}

/**
 * La dettatura non deve mai lasciare il focus sul pulsante del microfono.
 * Ogni superficie che riceve testo passa qui il suo campo: il secondo focus
 * al frame successivo copre anche i drawer/pannelli montati dallo stesso click.
 */
interface VoiceRecognitionOptions {
  focusTargetRef?: RefObject<HTMLElement | null>;
  /** Prepare the consumer's append base only after this hook accepts a new
   * capture attempt. A recognizer still stopping must not reset that base. */
  onSessionStart?: () => void;
}

const ERROR_MESSAGES: Record<string, string> = {
  "not-allowed": "Microfono bloccato dal browser. Se non compare la richiesta, abilita il microfono dalle impostazioni del sito/browser e riprova.",
  "no-speech": "Nessun audio rilevato. Riprova parlando più vicino al microfono.",
  "audio-capture": "Nessun microfono trovato.",
  "not-supported": "Dettatura vocale non supportata da questo browser.",
  network: "Errore di rete durante il riconoscimento vocale.",
};

async function requestMicrophoneAccess(): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    throw new DOMException(ERROR_MESSAGES["not-supported"], "NotSupportedError");
  }

  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach((track) => track.stop());
}

/**
 * Wrapper sulla Web Speech API del browser (gratuita, nessuna chiave
 * richiesta). continuous+interimResults=true per trascrizione in tempo
 * reale mentre l'utente parla. Fallback: se non supportata, isSupported
 * =false e l'utente digita il memo invece di parlare.
 */
export function useVoiceRecognition(
  onTranscriptUpdate: (finalText: string, interimText: string) => void,
  onSilence?: (transcript: string) => void,
  silenceMs = 4_500,
  options?: VoiceRecognitionOptions,
): UseVoiceRecognitionResult {
  const recognitionRef = useRef<any>(null);
  const generationRef = useRef(0);
  const startPendingRef = useRef(false);
  const finalTextRef = useRef<string>("");
  const intentionalStopRef = useRef(false);
  const silenceTimerRef = useRef<number | null>(null);
  const latestTranscriptRef = useRef("");
  const silenceCallbackRef = useRef(onSilence);
  const focusTargetRef = useRef(options?.focusTargetRef);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const SpeechRecognition =
    typeof window !== "undefined" ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition : null;
  const hasMicrophoneApi = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const isSupported = !!SpeechRecognition && hasMicrophoneApi;
  const onSessionStart = options?.onSessionStart;

  useEffect(() => {
    silenceCallbackRef.current = onSilence;
  }, [onSilence]);

  useEffect(() => {
    focusTargetRef.current = options?.focusTargetRef;
  }, [options?.focusTargetRef]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) window.clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
  }, []);

  const start = useCallback(async () => {
    if (!SpeechRecognition || isListening || startPendingRef.current || recognitionRef.current) return;
    const generation = ++generationRef.current;
    startPendingRef.current = true;
    onSessionStart?.();
    const focusTarget = () => focusTargetRef.current?.current?.focus({ preventScroll: true });
    focusTarget();
    window.requestAnimationFrame(focusTarget);
    setError(null);
    finalTextRef.current = "";
    latestTranscriptRef.current = "";
    intentionalStopRef.current = false;
    clearSilenceTimer();

    try {
      await requestMicrophoneAccess();
    } catch (permissionError) {
      if (generation !== generationRef.current) return;
      startPendingRef.current = false;
      setError(
        permissionError instanceof DOMException && permissionError.name === "NotFoundError"
          ? ERROR_MESSAGES["audio-capture"]
          : permissionError instanceof DOMException && permissionError.name === "NotSupportedError"
            ? ERROR_MESSAGES["not-supported"]
            : ERROR_MESSAGES["not-allowed"],
      );
      setIsListening(false);
      return;
    }
    if (generation !== generationRef.current) return;
    startPendingRef.current = false;

    const recognition = new SpeechRecognition();
    recognition.lang = "it-IT";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      if (generation !== generationRef.current) return;
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = normalizeItalianDictation(event.results[i][0].transcript);
        if (event.results[i].isFinal) {
          finalTextRef.current = appendItalianDictation(finalTextRef.current, transcript);
        } else {
          interim = appendItalianDictation(interim, transcript);
        }
      }
      latestTranscriptRef.current = appendItalianDictation(finalTextRef.current, interim);
      onTranscriptUpdate(finalTextRef.current, interim);
      clearSilenceTimer();
      if (latestTranscriptRef.current && silenceCallbackRef.current) {
        silenceTimerRef.current = window.setTimeout(() => {
          const transcript = latestTranscriptRef.current.trim();
          if (!transcript) return;
          intentionalStopRef.current = true;
          try { recognition.stop(); } catch {}
          silenceCallbackRef.current?.(transcript);
        }, silenceMs);
      }
    };
    recognition.onstart = () => {
      if (generation === generationRef.current) window.dispatchEvent(new Event("martina:dictation-start"));
    };
    recognition.onend = () => {
      if (generation !== generationRef.current) return;
      clearSilenceTimer();
      recognitionRef.current = null;
      setIsListening(false);
      window.dispatchEvent(new Event("martina:dictation-end"));
    };
    recognition.onerror = (event: any) => {
      if (generation !== generationRef.current) return;
      clearSilenceTimer();
      if (event.error === "aborted" && intentionalStopRef.current) {
        setError(null);
        return;
      }
      setError(ERROR_MESSAGES[event.error] || `Errore riconoscimento vocale (${event.error}).`);
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsListening(true);
    } catch (startError) {
      recognitionRef.current = null;
      setError(startError instanceof Error ? startError.message : "Impossibile avviare la dettatura vocale.");
      setIsListening(false);
    }
  }, [SpeechRecognition, clearSilenceTimer, isListening, onTranscriptUpdate, onSessionStart, silenceMs]);

  const stop = useCallback(() => {
    clearSilenceTimer();
    intentionalStopRef.current = true;
    if (startPendingRef.current) {
      generationRef.current++;
      startPendingRef.current = false;
    }
    try { recognitionRef.current?.stop(); } catch { recognitionRef.current = null; }
    setIsListening(false);
  }, [clearSilenceTimer]);

  // La chiusura del pannello, il cambio di fonte o una correzione manuale
  // invalidano anche i callback gia' accodati dal browser. `stop` invece
  // lascia arrivare l'ultimo risultato finale della normale dettatura.
  const cancel = useCallback(() => {
    const hadRecognition = Boolean(recognitionRef.current);
    generationRef.current++;
    startPendingRef.current = false;
    intentionalStopRef.current = true;
    clearSilenceTimer();
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    try { recognition?.abort(); } catch { /* la sessione e' gia' terminata */ }
    setIsListening(false);
    if (hadRecognition) window.dispatchEvent(new Event("martina:dictation-end"));
  }, [clearSilenceTimer]);

  useEffect(() => () => {
    generationRef.current++;
    startPendingRef.current = false;
    intentionalStopRef.current = true;
    clearSilenceTimer();
    try { recognitionRef.current?.abort(); } catch { /* il campo si sta chiudendo */ }
    recognitionRef.current = null;
  }, [clearSilenceTimer]);

  return { isSupported, isListening, error, start, stop, cancel };
}
